import { supabase } from './supabase'
import { isUsEquityEarlyCloseDay, isUsEquityTradingDay } from './marketSession'

// ── Supabase candle_cache service ───────────────────────────────────────────
// Persistent cross-device cache for IBKR historical bar data.
// One row per user + symbol + bar_size.

export async function getCachedCandles(symbol, barSize) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data, error } = await supabase
    .from('candle_cache')
    .select('bars, fetched_at, latest_bar_time')
    .eq('user_id', user.id)
    .eq('symbol', symbol.toUpperCase())
    .eq('bar_size', barSize)
    .maybeSingle()

  if (error || !data) return null
  return {
    bars: data.bars,
    fetchedAt: data.fetched_at,
    latestBarTime: data.latest_bar_time,
  }
}

export async function getCachedCandleMeta(symbol, barSize) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data, error } = await supabase
    .from('candle_cache')
    .select('fetched_at, latest_bar_time, bar_count')
    .eq('user_id', user.id)
    .eq('symbol', symbol.toUpperCase())
    .eq('bar_size', barSize)
    .maybeSingle()

  if (error || !data) return null
  return {
    fetchedAt: data.fetched_at,
    latestBarTime: data.latest_bar_time,
    barCount: data.bar_count,
  }
}

export async function setCachedCandles(symbol, barSize, bars) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user || !bars?.length) return

  const latest = bars[bars.length - 1]
  const latestBarTime = typeof latest.time === 'string' ? latest.time : String(latest.time)

  const { error } = await supabase
    .from('candle_cache')
    .upsert({
      user_id: user.id,
      symbol: symbol.toUpperCase(),
      bar_size: barSize,
      bars,
      bar_count: bars.length,
      latest_bar_time: latestBarTime,
      fetched_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,symbol,bar_size' })

  if (error) console.error('[candleCache] upsert error:', error.message)
}

/**
 * Merge new bars into existing cached bars (append-only).
 * Deduplicates by timestamp, keeps the newer bar on collision, sorts chronologically.
 */
export async function mergeCachedCandles(symbol, barSize, newBars) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return

  // EF-133: when called with no newBars (e.g. fetchBars' Tier 3 returned
  // empty), return the existing cached bars deduplicated + sorted instead
  // of returning undefined. Returning undefined causes fetchBars to fall
  // through to the `||` fallback `[...cached.bars, ...newBars]`, which
  // hands raw JSONB-ordered cache data to LWC; if that data has duplicate
  // timestamps or non-ascending order, LWC silently renders nothing.
  if (!newBars?.length) {
    const { data } = await supabase
      .from('candle_cache')
      .select('bars')
      .eq('user_id', user.id)
      .eq('symbol', symbol.toUpperCase())
      .eq('bar_size', barSize)
      .maybeSingle()
    const existing = data?.bars || []
    if (!existing.length) return
    const byTime = new Map()
    for (const bar of existing) byTime.set(bar.time, bar)
    return [...byTime.values()].sort((a, b) => a.time - b.time)
  }

  const { data } = await supabase
    .from('candle_cache')
    .select('bars')
    .eq('user_id', user.id)
    .eq('symbol', symbol.toUpperCase())
    .eq('bar_size', barSize)
    .maybeSingle()

  const existingBars = data?.bars || []

  const byTime = new Map()
  for (const bar of existingBars) byTime.set(bar.time, bar)
  for (const bar of newBars) byTime.set(bar.time, bar)
  const merged = [...byTime.values()].sort((a, b) => a.time - b.time)

  const latest = merged[merged.length - 1]
  const latestBarTime = typeof latest.time === 'string' ? latest.time : String(latest.time)

  const { error } = await supabase
    .from('candle_cache')
    .upsert({
      user_id: user.id,
      symbol: symbol.toUpperCase(),
      bar_size: barSize,
      bars: merged,
      bar_count: merged.length,
      latest_bar_time: latestBarTime,
      fetched_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,symbol,bar_size' })

  if (error) console.error('[candleCache] merge error:', error.message)
  return merged
}

// ── Freshness logic ─────────────────────────────────────────────────────────
// Market hours: 9:30–16:00 ET, Mon–Fri

const MARKET_OPEN_MIN_ET = 9 * 60 + 30
const DAILY_BAR_OPEN_GRACE_MIN_ET = 9 * 60 + 35
const MARKET_CLOSE_MIN_ET = 16 * 60
const EARLY_CLOSE_MIN_ET = 13 * 60
const AFTER_HOURS_CLOSE_MIN_ET = 20 * 60
const DAILY_BAR_CLOSE_GRACE_MIN_ET = 16 * 60 + 5
const DAILY_INTRADAY_TTL_MS = 5 * 60 * 1000
const INTRADAY_BAR_GRACE_MIN = 2
const INTRADAY_BAR_MINUTES = {
  '1 min': 1,
  '2 mins': 2,
  '5 mins': 5,
  '15 mins': 15,
  '30 mins': 30,
  '65 mins': 65,
}

function getNowET() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
    weekday: 'short',
  }).formatToParts(new Date())

  const get = type => parts.find(p => p.type === type)?.value
  return {
    hour: +get('hour'),
    minute: +get('minute'),
    weekday: get('weekday'),
    dateStr: `${get('year')}-${get('month')}-${get('day')}`,
  }
}

function getEtDateAndMinute(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
    weekday: 'short',
  }).formatToParts(date)
  const get = type => parts.find(p => p.type === type)?.value
  const hour = parseInt(get('hour') || '0', 10)
  const minute = parseInt(get('minute') || '0', 10)
  return {
    dateStr: `${get('year')}-${get('month')}-${get('day')}`,
    minuteOfDay: (hour === 24 ? 0 : hour) * 60 + minute,
    weekday: get('weekday'),
  }
}

function latestIntradayEt(latestBarTime) {
  if (latestBarTime == null) return null
  if (typeof latestBarTime === 'string') {
    const m = latestBarTime.match(/^(\d{4})(\d{2})(\d{2})\s+(\d{2}):(\d{2}):/)
    if (m) {
      const [, y, mo, d, h, mi] = m
      return {
        dateStr: `${y}-${mo}-${d}`,
        minuteOfDay: Number(h) * 60 + Number(mi),
      }
    }
  }
  let raw = null
  if (typeof latestBarTime === 'number' && Number.isFinite(latestBarTime)) {
    raw = latestBarTime
  } else if (typeof latestBarTime === 'string' && /^\d+$/.test(latestBarTime)) {
    raw = Number(latestBarTime)
  }
  if (!raw || !Number.isFinite(raw)) return null
  const ms = raw > 1e12 ? raw : raw * 1000
  return getEtDateAndMinute(new Date(ms))
}

function marketCloseMinute(dateStr) {
  const date = dateStrToUtcNoon(dateStr)
  return date && isUsEquityEarlyCloseDay(date) ? EARLY_CLOSE_MIN_ET : MARKET_CLOSE_MIN_ET
}

function dateStrToUtc(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function dateStrToUtcNoon(dateStr) {
  const date = dateStrToUtc(dateStr)
  if (Number.isNaN(date.getTime())) return null
  date.setUTCHours(12, 0, 0, 0)
  return date
}

function addDays(dateStr, days) {
  const d = dateStrToUtc(dateStr)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function isTradingDate(dateStr) {
  const date = dateStrToUtcNoon(dateStr)
  return Boolean(date && isUsEquityTradingDay(date))
}

function previousTradingDate(dateStr) {
  let cursor = addDays(dateStr, -1)
  while (!isTradingDate(cursor)) cursor = addDays(cursor, -1)
  return cursor
}

function expectedIntradayBarStart(nowMinute, barMinutes, closeMinute = MARKET_CLOSE_MIN_ET) {
  if (!barMinutes) return null
  if (nowMinute >= closeMinute) return closeMinute - barMinutes
  if (nowMinute < MARKET_OPEN_MIN_ET) return null
  const elapsed = nowMinute - MARKET_OPEN_MIN_ET
  const currentStart = MARKET_OPEN_MIN_ET + Math.floor(elapsed / barMinutes) * barMinutes
  const minutesIntoBar = nowMinute - currentStart
  if (minutesIntoBar < INTRADAY_BAR_GRACE_MIN) {
    const previousStart = currentStart - barMinutes
    return previousStart >= MARKET_OPEN_MIN_ET ? previousStart : null
  }
  return currentStart
}

function isMarketOpen(et) {
  const weekend = et.weekday === 'Sat' || et.weekday === 'Sun'
  if (weekend) return false
  if (!isTradingDate(et.dateStr)) return false
  const mins = et.hour * 60 + et.minute
  return mins >= MARKET_OPEN_MIN_ET && mins < marketCloseMinute(et.dateStr)
}

function latestDailyDateKey(latestBarTime) {
  if (typeof latestBarTime === 'string' && /^\d{8}$/.test(latestBarTime)) return latestBarTime
  if (
    typeof latestBarTime === 'number'
    && Number.isFinite(latestBarTime)
    && Number.isInteger(latestBarTime)
    && latestBarTime >= 19000101
    && latestBarTime <= 99991231
  ) {
    return String(latestBarTime)
  }
  return null
}

function lastRequiredIntradayBar(now, barMinutes) {
  const et = getEtDateAndMinute(now)
  if (!isTradingDate(et.dateStr)) {
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }

  const closeMinute = marketCloseMinute(et.dateStr)
  if (et.minuteOfDay < MARKET_OPEN_MIN_ET) {
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }
  if (et.minuteOfDay >= closeMinute) {
    return {
      dateStr: et.dateStr,
      minuteOfDay: closeMinute - barMinutes,
    }
  }

  const expectedStart = expectedIntradayBarStart(et.minuteOfDay, barMinutes, closeMinute)
  if (expectedStart == null) {
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }
  if (barMinutes === 65) {
    const previousStart = expectedStart - barMinutes
    if (previousStart >= MARKET_OPEN_MIN_ET) {
      return {
        dateStr: et.dateStr,
        minuteOfDay: previousStart,
      }
    }
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }
  return {
    dateStr: et.dateStr,
    minuteOfDay: expectedStart,
  }
}

function reachesIntradayTarget(latestEt, target) {
  if (!latestEt || !target) return false
  if (latestEt.dateStr > target.dateStr) return true
  if (latestEt.dateStr < target.dateStr) return false
  return latestEt.minuteOfDay >= target.minuteOfDay
}

export function cacheReachesLastSession(latestBarTime, barSize, now = new Date()) {
  const barMinutes = INTRADAY_BAR_MINUTES[barSize] || null
  if (!barMinutes) return true
  return reachesIntradayTarget(
    latestIntradayEt(latestBarTime),
    lastRequiredIntradayBar(now, barMinutes)
  )
}

export function barsReachLastSession(bars, barSize, now = new Date()) {
  const barMinutes = INTRADAY_BAR_MINUTES[barSize] || null
  if (!barMinutes) return true
  if (!bars?.length) return false
  const target = lastRequiredIntradayBar(now, barMinutes)
  return bars.some(bar => reachesIntradayTarget(latestIntradayEt(bar?.time), target))
}

function lastCompletedIntradayBar(now, barMinutes) {
  const et = getEtDateAndMinute(now)
  if (!isTradingDate(et.dateStr)) {
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }

  const closeMinute = marketCloseMinute(et.dateStr)
  if (et.minuteOfDay < MARKET_OPEN_MIN_ET) {
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }
  if (et.minuteOfDay >= closeMinute) {
    return {
      dateStr: et.dateStr,
      minuteOfDay: closeMinute - barMinutes,
    }
  }

  const currentStart = expectedIntradayBarStart(et.minuteOfDay, barMinutes, closeMinute)
  if (currentStart == null) {
    const targetDate = previousTradingDate(et.dateStr)
    return {
      dateStr: targetDate,
      minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
    }
  }
  const completedStart = currentStart - barMinutes
  if (completedStart >= MARKET_OPEN_MIN_ET) {
    return {
      dateStr: et.dateStr,
      minuteOfDay: completedStart,
    }
  }
  const targetDate = previousTradingDate(et.dateStr)
  return {
    dateStr: targetDate,
    minuteOfDay: marketCloseMinute(targetDate) - barMinutes,
  }
}

function postCloseProviderLagTarget(now, barMinutes) {
  const et = getEtDateAndMinute(now)
  if (!isTradingDate(et.dateStr)) return null
  const closeMinute = marketCloseMinute(et.dateStr)
  if (et.minuteOfDay < closeMinute || et.minuteOfDay >= AFTER_HOURS_CLOSE_MIN_ET) return null
  const previousStart = closeMinute - (barMinutes * 2)
  if (previousStart < MARKET_OPEN_MIN_ET) return null
  return {
    dateStr: et.dateStr,
    minuteOfDay: previousStart,
  }
}

export function barsReachLastCompletedSession(bars, barSize, now = new Date()) {
  const barMinutes = INTRADAY_BAR_MINUTES[barSize] || null
  if (!barMinutes) return true
  if (!bars?.length) return false
  const target = lastCompletedIntradayBar(now, barMinutes)
  if (bars.some(bar => reachesIntradayTarget(latestIntradayEt(bar?.time), target))) return true
  const lagTarget = postCloseProviderLagTarget(now, barMinutes)
  return Boolean(lagTarget && bars.some(bar => reachesIntradayTarget(latestIntradayEt(bar?.time), lagTarget)))
}

export function isCacheFresh(fetchedAt, barSize, latestBarTime = null) {
  if (!fetchedAt) return false
  const et = getNowET()
  const fetchedDate = new Date(fetchedAt)

  // Get fetched time in ET
  const fetchedParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(fetchedDate)
  const fGet = type => fetchedParts.find(p => p.type === type)?.value
  const fetchedDateStr = `${fGet('year')}-${fGet('month')}-${fGet('day')}`

  const ageMs = Date.now() - fetchedDate.getTime()

  if (barSize === '1 day') {
    // EF-132: a fetch made before market open (or pre-market) does NOT
    // include today's bar — IBKR's RTH-only feed only emits today's bar
    // once trading begins. The old "fresh if fetched today" check kept
    // such caches alive past the open and the chart never picked up the
    // forming daily candle.
    //
    // Current rule:
    //   - Fetched on a previous day → stale (always)
    //   - During RTH, require a same-day latest bar when present and only
    //     reuse a short-lived cache row. IBKR historical daily can lag the
    //     intraday/live chart, so an all-day "fresh" row is unsafe.
    //   - After close, require a fetch after the 4:05 PM ET settle grace
    //     and a same-day latest bar when present. This prevents a midday
    //     partial daily candle from surviving into post-market.
    if (fetchedDateStr !== et.dateStr) return false
    const latestKey = latestDailyDateKey(latestBarTime)
    const todayKey = et.dateStr.replace(/-/g, '')
    const weekend = et.weekday === 'Sat' || et.weekday === 'Sun'
    const nowMinute = et.hour * 60 + et.minute
    const fetchedEt = getEtDateAndMinute(fetchedDate)
    if (!weekend && nowMinute >= MARKET_OPEN_MIN_ET && latestKey && latestKey !== todayKey) {
      return false
    }
    if (isMarketOpen(et)) {
      if (fetchedEt.minuteOfDay < DAILY_BAR_OPEN_GRACE_MIN_ET) return false
      return ageMs < DAILY_INTRADAY_TTL_MS
    }
    if (!weekend && nowMinute >= MARKET_CLOSE_MIN_ET) {
      return fetchedEt.minuteOfDay >= DAILY_BAR_CLOSE_GRACE_MIN_ET
    }
    return true
  }

  if (barSize === '1 week') {
    if (fetchedDateStr !== et.dateStr) return false
    if (isMarketOpen(et)) {
      const fetchedEt = getEtDateAndMinute(fetchedDate)
      if (fetchedEt.minuteOfDay < DAILY_BAR_OPEN_GRACE_MIN_ET) return false
    }
    return true
  }

  const intradayBarMinutes = INTRADAY_BAR_MINUTES[barSize] || null
  if (!isMarketOpen(et)) {
    // Outside market hours: fresh if fetched after last close (same day after 4pm, or any time on weekend)
    if (fetchedDateStr === et.dateStr) {
      const weekend = et.weekday === 'Sat' || et.weekday === 'Sun'
      const nowMinute = et.hour * 60 + et.minute
      if (intradayBarMinutes && !weekend && nowMinute >= marketCloseMinute(et.dateStr)) {
        const latestEt = latestIntradayEt(latestBarTime)
        const finalBarStart = marketCloseMinute(et.dateStr) - intradayBarMinutes
        if (latestEt) return latestEt.dateStr === et.dateStr && latestEt.minuteOfDay >= finalBarStart
        const fetchedEt = getEtDateAndMinute(fetchedDate)
        return fetchedEt.minuteOfDay >= DAILY_BAR_CLOSE_GRACE_MIN_ET
      }
      return true
    }
    // Fetched on a previous day — stale if market has opened since
    const weekend = et.weekday === 'Sat' || et.weekday === 'Sun'
    if (weekend) {
      // On weekend, fresh if fetched on Friday or later
      return (
        ageMs < 3 * 24 * 60 * 60 * 1000
        && cacheReachesLastSession(latestBarTime, barSize)
      )
    }
    return intradayBarMinutes
      ? cacheReachesLastSession(latestBarTime, barSize)
      : false
  }

  // During market hours
  if (intradayBarMinutes) {
    if (fetchedDateStr !== et.dateStr) return false
    const nowMinute = et.hour * 60 + et.minute
    const expectedStart = expectedIntradayBarStart(nowMinute, intradayBarMinutes)
    if (expectedStart != null) {
      const latestEt = latestIntradayEt(latestBarTime)
      if (latestEt) {
        if (latestEt.dateStr !== et.dateStr || latestEt.minuteOfDay < expectedStart) return false
      } else {
        const fetchedEt = getEtDateAndMinute(fetchedDate)
        if (fetchedEt.minuteOfDay < expectedStart + INTRADAY_BAR_GRACE_MIN) return false
      }
    }
    return ageMs < intradayBarMinutes * 60 * 1000
  }
  // EF-132: 5-min cache TTL tightened from 15 min → 5 min so the cache
  // refreshes on every actual 5-min bar boundary instead of letting the
  // forming bar fall behind for up to a third of its lifetime. 15-mins
  // bars still get the wider window via the same return.
  if (barSize === '5 mins') return ageMs < 5 * 60 * 1000
  // 15-min bars
  return ageMs < 15 * 60 * 1000
}

export async function clearCacheForSymbol(symbol) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return
  await supabase
    .from('candle_cache')
    .delete()
    .eq('user_id', user.id)
    .eq('symbol', symbol.toUpperCase())
}

export async function clearAllCache() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return
  await supabase
    .from('candle_cache')
    .delete()
    .eq('user_id', user.id)
}
